export const addAnalyticsAttribute = (node, event) => {
  if (!node) {
    return false;
  }

  node.dataset.eventCategory = event.category;
  node.dataset.eventAction = event.action;
  node.dataset.eventLabel = event.label;
  node.dataset.on = event.on || 'click';

  return node;
};

// options string format: "key1:value1|key2:value2"
export const getFormattedOptions = (serializedOptions) => {
  if (!serializedOptions) {
    return {};
  }

  return serializedOptions.split('|').reduce((acc, v) => {
    const [key, value] = v.split(':');
    // buildingId always must be number
    const newValue = key === 'buildingId' ? Number(value) : value;

    return {
      ...acc,
      [key]: newValue,
    };
  }, {});
};

const isVisible = (node) => {
  const rect = node.getBoundingClientRect();
  const elemTop = rect.top;
  const elemBottom = rect.bottom;
  return elemTop > 0 && elemBottom <= window.innerHeight;
}

export const sendEvent = (
  eventCategory,
  eventAction,
  eventLabel,
  eventOptions,
  callback
) => {
  const ec = eventCategory || window.eventCategory;
  // to send events to a new property pass `routeToNewGa4:true` to eventOptions
  const { routeToNewGa4, ...options } = getFormattedOptions(eventOptions);

  ga('send', 'event', {
    eventCategory: ec,
    eventAction,
    eventLabel,
    nonInteraction: true,
    event_callback: callback,
  });
  window.dataLayer.push({
    event: 'lun.ua',
    eventCategory: ec,
    eventAction,
    eventLabel,
  });

  gtag('event', eventLabel, {
    pageType: ec,
    placeOnPage: eventAction,
    language: window.currentLanguage === 'uk' ? 'ukr' : 'ru',
    backendSplitTestVersion: window.splitTestVersion,
    route_to_new_ga4: Boolean(routeToNewGa4),
    ...window.analyticsData,
    ...options,
  });
};

export const initShownAnalytics = (scrollNode = window) => {
  let scrollTimeout = null;

  scrollNode.addEventListener('scroll', () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      const nodes = document.querySelectorAll('[data-event-shown]');
      [].forEach.call(nodes, (node) => {
        if (isVisible(node)) {
          const eventLabel = node.getAttribute('data-event-shown') || `${node.getAttribute('data-event-label')}_shown`;
          node.removeAttribute('data-event-shown');
          sendEvent(
            node.getAttribute('data-event-category'),
            node.getAttribute('data-event-action'),
            eventLabel,
            node.getAttribute('data-event-options'),
          );
        }
      });
    }, 250);
  });
};

const COMMUNICATION_ANALYTICS_ATTRIBUTE = 'data-communication-analytics';

const initCommunicationAnalytics = () => {
  document.addEventListener(
    'click',
    (event) => {
      const node = event.target.closest(`[${COMMUNICATION_ANALYTICS_ATTRIBUTE}]`);
      if (!node) return;

      try {
        const { event_name: eventName, ...options } = JSON.parse(
          node.getAttribute(COMMUNICATION_ANALYTICS_ATTRIBUTE),
        );
        gtag('event', eventName, {
          route_to_new_ga4: true,
          ...options,
        });
      } catch (error) {
        console.warn(error);
      }
    },
    { capture: true },
  );
};

export const initAnalytics = () => {
  document.addEventListener(
    'click',
    (event) => {
      const nodes = document.querySelectorAll('[data-on="click"]');
      [].forEach.call(nodes, (node) => {
        // Elements marked with data-ignore-click should not trigger parent analytics click events
        if (event.target.closest('[data-ignore-click]')) {
          return;
        }
        if (node.contains(event.target)) {
          sendEvent(
            node.getAttribute('data-event-category'),
            node.getAttribute('data-event-action'),
            node.getAttribute('data-event-label'),
            node.getAttribute('data-event-options'),
          );
        }
      });
    },
    { capture: true },
  );

  document.addEventListener('copy', (event) => {
    const nodes = document.querySelectorAll('[data-on="copy"]');
    [].forEach.call(nodes, (node) => {
      if (node.contains(event.target)) {
        sendEvent(
          node.getAttribute('data-event-category'),
          node.getAttribute('data-event-action'),
          node.getAttribute('data-event-label'),
          node.getAttribute('data-event-options'),
        );
      }
    });
  });

  initShownAnalytics();
  initCommunicationAnalytics();
};

export const attachMapAnalytics = (map) => {
  sendEvent(window.eventCategory, 'map', 'map_initialized');

  const compass = map.querySelector('.mapboxgl-ctrl-compass');
  if (compass) {
    compass.addEventListener('click', () => {
      sendEvent(window.eventCategory, 'map_controls', 'compass');
    });
  }
  const location = map.querySelector('.mapboxgl-ctrl-geolocate');
  if (location) {
    location.addEventListener('click', () => {
      sendEvent(window.eventCategory, 'map_controls', 'locate_geo');
    });
  }
  const ruler = map.querySelector('.mapboxgl-ctrl-ruler');
  if (ruler) {
    ruler.addEventListener('click', () => {
      sendEvent(window.eventCategory, 'map_controls', 'ruler');
    });
  }
  const zoom = map.querySelectorAll('.mapboxgl-ctrl-zoom button');
  if (zoom.length) {
    zoom[0].addEventListener('click', () => {
      sendEvent(window.eventCategory, 'map_controls', 'zoom_in');
    });
    zoom[1].addEventListener('click', () => {
      sendEvent(window.eventCategory, 'map_controls', 'zoom_out');
    });
  }
  const styles = map.querySelectorAll('.mapboxgl-ctrl-styles button');
  if (styles.length > 1) {
    styles[0].addEventListener('click', () => {
      sendEvent(window.eventCategory, 'map_controls', 'streets_click');
    });
    styles[1].addEventListener('click', () => {
      sendEvent(window.eventCategory, 'map_controls', 'satellite_click');
    });
  } else {
    sendEvent(window.eventCategory, 'map_controls', 'layers_click');
  }
  const around = map.querySelector('.mapboxgl-ctrl-around');
  if (around) {
    around.addEventListener('click', () => {
      sendEvent(window.eventCategory, 'map_controls', 'around');
    });
  }
};

export const attachGaDataAttributes = (event) => {
  if (!event) return {};
  return {
    "data-on": "click",
    "data-event-category": event.category,
    "data-event-action": event.action,
    "data-event-label": event.label || "click",
  };
};
